import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip, Paper } from '@mui/material';
import { Error, CheckCircle, Schedule, ConfirmationNumber, Sync, Storage } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import api from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import EmpresaSelect from '../../../components/EmpresaSelect';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#9c27b0', '#0288d1', '#f44336', '#ff9800', '#4caf50', '#795548', '#607d8b', '#e91e63'];

const white = { bgcolor: '#fff', color: '#333' }

const getItem = (storage, key) => {
    const d = localStorage.getItem(storage)
    return d ? (JSON.parse(d)[key] ?? null) : null
}

export default function Dashboard() {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [empresa, setEmpresa] = useState(getItem('hermes_dashboard', 'empresa') || null)
    const navigate = useNavigate()

    useEffect(() => {
        document.title = "IPV7 Hermes - Dashboard"
        fetchData(empresa)
    }, [empresa])

    const fetchData = async (emp) => {
        setLoading(true)
        try {
            const params = emp ? { empresa: emp } : {}
            const res = await api.get('/admin/hermes/backup/stats/dashboard', { params })
            setData(res.data)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}><CircularProgress /></Box>
    }
    if (!data) {
        return <Typography color="error" sx={{ p: 2 }}>Erro ao carregar dashboard</Typography>
    }

    const cards = [
        { label: 'Total Hosts', value: data.total_hosts ?? 0, icon: <Storage />, color: '#1976d2' },
        { label: 'Com Erro', value: data.hosts_with_error ?? 0, icon: <Error />, color: '#d32f2f', onClick: () => nav('error') },
        { label: 'Sem Backup', value: data.hosts_without_backup ?? 0, icon: <Schedule />, color: '#ed6c02', onClick: () => nav('new') },
        { label: 'OK', value: data.hosts_ok ?? 0, icon: <CheckCircle />, color: '#2e7d32', onClick: () => nav('ok') },
        { label: 'Executando', value: data.executing_now ?? 0, icon: <Sync />, color: '#0288d1' },
        { label: 'Tickets', value: data.tickets_open ?? 0, icon: <ConfirmationNumber />, color: '#ff9800' },
    ]

    const nav = (filter) => {
        const stored = JSON.parse(localStorage.getItem('hermes') || '{}')
        localStorage.setItem('hermes', JSON.stringify({ ...stored, backup_filter: filter }))
        navigate('/admin/hermes/host')
    }

    const navEmpresa = (empresaId) => {
        const stored = JSON.parse(localStorage.getItem('hermes') || '{}')
        localStorage.setItem('hermes', JSON.stringify({ ...stored, empresa: empresaId, backup_filter: 'error' }))
        navigate('/admin/hermes/host')
    }

    return (
        <Box sx={{ p: 2, ...white, minHeight: '100vh' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#333' }}>Dashboard — Serviço de Backup</Typography>
                <EmpresaSelect
                    sx={{ minWidth: 300 }}
                    value={empresa}
                    handler={(e) => {
                        localStorage.setItem('hermes_dashboard', JSON.stringify({ empresa: e }))
                        setEmpresa(e)
                    }}
                />
            </Box>

            {/* Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {cards.map((c) => (
                    <Grid item xs={6} sm={4} md={2} key={c.label}>
                        <Card variant="outlined" sx={{
                            borderLeft: `4px solid ${c.color}`, bgcolor: '#fff',
                            cursor: c.onClick ? 'pointer' : 'default',
                            '&:hover': c.onClick ? { boxShadow: 3 } : {}
                        }} onClick={c.onClick}>
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box sx={{ color: c.color, opacity: 0.7 }}>{c.icon}</Box>
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold" color={c.color}>{c.value}</Typography>
                                        <Typography variant="caption" sx={{ color: '#888' }}>{c.label}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Row 1: Execution 7d + Status donut */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Execução — Últimos 7 dias</Typography>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={data.history_7d || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="date" fontSize={12} tick={{ fill: '#666' }} />
                                <YAxis fontSize={12} tick={{ fill: '#666' }} />
                                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                                <Bar dataKey="success" name="Sucesso" fill="#4caf50" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="failed" name="Falha" fill="#f44336" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Status dos Hosts</Typography>
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'OK', value: data.hosts_ok ?? 0 },
                                        { name: 'Com Erro', value: data.hosts_with_error ?? 0 },
                                        { name: 'Sem Backup', value: data.hosts_without_backup ?? 0 },
                                    ].filter(d => d.value > 0)}
                                    cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                                    paddingAngle={3} dataKey="value"
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                    <Cell fill="#4caf50" />
                                    <Cell fill="#f44336" />
                                    <Cell fill="#ff9800" />
                                </Pie>
                                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Execution by empresa (line chart — only in global view) */}
            {(data.history_by_empresa || []).length > 0 && (data.empresa_names || []).length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fff' }}>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Execução por Empresa — Top 10 (7 dias)</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={data.history_by_empresa}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="date" fontSize={12} tick={{ fill: '#666' }} />
                            <YAxis fontSize={12} tick={{ fill: '#666' }} />
                            <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {(data.empresa_names || []).map((name, i) => (
                                <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]}
                                    strokeWidth={2} dot={false} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Paper>
            )}

            {/* Row 2: Vendor + Type + Protocol — horizontal bars */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Por Vendor</Typography>
                        <ResponsiveContainer width="100%" height={Math.max(200, (data.by_vendor || []).length * 32)}>
                            <BarChart data={(data.by_vendor || []).slice(0, 12)} layout="vertical" margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                                <XAxis type="number" fontSize={11} tick={{ fill: '#666' }} />
                                <YAxis type="category" dataKey="vendor" width={90} fontSize={11} tick={{ fill: '#333' }} />
                                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                                <Bar dataKey="count" name="Hosts" fill="#1976d2" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Por Tipo</Typography>
                        <ResponsiveContainer width="100%" height={Math.max(200, (data.by_type || []).length * 32)}>
                            <BarChart data={(data.by_type || []).slice(0, 12)} layout="vertical" margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                                <XAxis type="number" fontSize={11} tick={{ fill: '#666' }} />
                                <YAxis type="category" dataKey="type" width={100} fontSize={11} tick={{ fill: '#333' }} />
                                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                                <Bar dataKey="count" name="Hosts" fill="#2e7d32" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, color: '#333' }}>Por Protocolo</Typography>
                        <ResponsiveContainer width="100%" height={Math.max(150, (data.by_protocol || []).length * 40)}>
                            <BarChart data={(data.by_protocol || []).slice(0, 5)} layout="vertical" margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                                <XAxis type="number" fontSize={11} tick={{ fill: '#666' }} />
                                <YAxis type="category" dataKey="protocol" width={70} fontSize={12} tick={{ fill: '#333' }} />
                                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd' }} />
                                <Bar dataKey="count" name="Hosts" fill="#ed6c02" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* 24h chips */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#888' }}>Últimas 24 horas</Typography>
                <Box display="flex" gap={2}>
                    <Chip label={`${data.last_24h?.success ?? 0} backups salvos`} color="success" variant="outlined" />
                    <Chip label={`${data.last_24h?.failed ?? 0} falhas`} color="error" variant="outlined" />
                    <Chip label={`${data.last_24h?.total ?? 0} execuções`} variant="outlined" />
                </Box>
            </Box>

            {/* Tables */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ p: 2, pb: 0, color: '#333' }}>Top Erros</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell sx={{ color: '#666' }}>Mensagem</TableCell><TableCell align="right" sx={{ color: '#666' }}>Qtd</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.top_errors || []).map((err, i) => (
                                    <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {err.message}
                                        </TableCell>
                                        <TableCell align="right"><Chip size="small" label={err.count} color="error" variant="outlined" /></TableCell>
                                    </TableRow>
                                ))}
                                {(data.top_errors || []).length === 0 && (
                                    <TableRow><TableCell colSpan={2} align="center" sx={{ color: '#aaa' }}>Nenhum erro</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ bgcolor: '#fff' }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ p: 2, pb: 0, color: '#333' }}>Top Empresas com Erros</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell sx={{ color: '#666' }}>Empresa</TableCell><TableCell align="right" sx={{ color: '#666' }}>Erros</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.top_empresas_with_errors || []).map((emp, i) => (
                                    <TableRow key={i} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#fafafa' } }}
                                        onClick={() => navEmpresa(emp.empresa)}>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{emp.name}</TableCell>
                                        <TableCell align="right"><Chip size="small" label={emp.error_count} color="warning" variant="outlined" /></TableCell>
                                    </TableRow>
                                ))}
                                {(data.top_empresas_with_errors || []).length === 0 && (
                                    <TableRow><TableCell colSpan={2} align="center" sx={{ color: '#aaa' }}>Nenhuma empresa com erros</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    )
}
